import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert,
  Stack, Chip, FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel, Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  getTrustPointQueue,
  backupAdjustments,
  getTrustPoints,
  previewTrustPoint,
  applyTrustPoint,
  acceptCurrentTrustPoint,
  getDailyAggregatedMovements,
  getCalculatedStockLevels,
  getStockBreakdownFromTrustPoint,
  formatNumber,
} from '../../services/inventoryService';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

export default function TrustPointWizard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Direct mode: explicit ?direct=1 invocation (currently only from /inventory/movements).
  // Synthesizes a single-item queue from the URL params and skips all queue-load /
  // dropdown-population effects. Header, queue chrome, and Skip/Back are hidden.
  //
  // NOTE: presence of sku+warehouse+brand_id in the URL ALONE no longer triggers direct
  // mode — those params now act as a "jump to this item in the queue" hint so the URL
  // stays shareable (queue chrome remains, user can browse Next/Previous from there).
  // Direct mode requires the explicit ?direct=1 flag.
  const directSku = searchParams.get('sku');
  const directWarehouse = searchParams.get('warehouse');
  const directBrandId = searchParams.get('brand_id');
  const isDirectMode = searchParams.get('direct') === '1'
    && !!(directSku && directWarehouse && directBrandId);
  const directItem = useMemo(() => (
    isDirectMode
      ? { sku: directSku, warehouse_code: directWarehouse, brand_id: parseInt(directBrandId, 10) }
      : null
  ), [isDirectMode, directSku, directWarehouse, directBrandId]);

  // Tracks whether the user has successfully applied/accepted in direct mode, so we
  // can swap the apply panel for a success card with a back-to-movements link.
  const [directDone, setDirectDone] = useState(false);

  // Queue state
  // Direct mode seeds the queue asynchronously (see effect below) once the brand's
  // acquisition_date is resolved, so the synthesized item carries it — exactly like the
  // queue endpoint does in normal mode. Start empty + loading until that resolves.
  const [queue, setQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [queueError, setQueueError] = useState(null);
  const [hideProcessed, setHideProcessed] = useState(
    () => localStorage.getItem('tpw.hideProcessed') === 'true'
  );
  const [includeBundles, setIncludeBundles] = useState(
    () => localStorage.getItem('tpw.includeBundles') === 'true'
  );
  // Filter state — null = show all
  const [filterBrandId, setFilterBrandId] = useState(
    () => { const v = localStorage.getItem('tpw.filterBrandId'); return v ? parseInt(v, 10) : null; }
  );
  const [filterWarehouse, setFilterWarehouse] = useState(
    () => localStorage.getItem('tpw.filterWarehouse') || null
  );
  // Static lists for dropdowns — derived from an UNFILTERED queue load on mount
  const [allBrandOptions, setAllBrandOptions] = useState([]);   // [{id, name}]
  const [allWarehouseOptions, setAllWarehouseOptions] = useState([]); // [code]

  // Backup state
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);

  // Navigation
  const [currentIndex, setCurrentIndex] = useState(0);

  // Per-card state — refetched every time the user moves to a new card
  const [trustPointsData, setTrustPointsData] = useState(null);
  const [tpLoading, setTpLoading] = useState(false);
  const [tpError, setTpError] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);

  // Prefetch cache for the NEXT product's trust-point candidate list, so stepping
  // forward is instant. Keyed by `${brand_id}|${sku}|${warehouse_code}`. Holds
  // getTrustPoints results only (cheap) — variance previews still load on arrival,
  // so this adds no load to the OOM-sensitive preview path. Capped to bound memory.
  const prefetchCacheRef = useRef(new Map());

  // Populate dropdown options ONCE from an unfiltered queue load — so the brand/warehouse lists
  // don't shrink when the user picks a filter.
  useEffect(() => {
    if (isDirectMode) return undefined; // direct mode shows no filters
    let cancelled = false;
    getTrustPointQueue({})
      .then(resp => {
        if (cancelled || !resp?.success) return;
        const rows = resp.data || [];
        const brands = new Map();
        const warehouses = new Set();
        for (const r of rows) {
          if (r.brand_id) brands.set(r.brand_id, r.brand_name || `Brand ${r.brand_id}`);
          if (r.warehouse_code) warehouses.add(r.warehouse_code);
        }
        setAllBrandOptions([...brands.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
        setAllWarehouseOptions([...warehouses].sort());
      })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [isDirectMode]);

  // Load filtered queue when any filter changes
  useEffect(() => {
    if (isDirectMode) return undefined; // direct mode bypasses the queue entirely
    let cancelled = false;
    setLoadingQueue(true);
    setQueueError(null);
    setCurrentIndex(0);
    const params = { hide_processed: hideProcessed, include_bundles: includeBundles };
    if (filterBrandId) params.brand_id = filterBrandId;
    if (filterWarehouse) params.warehouse_code = filterWarehouse;
    getTrustPointQueue(params)
      .then(resp => {
        if (cancelled) return;
        if (resp?.success) setQueue(resp.data || []);
        else setQueueError(resp?.error || 'Failed to load queue');
      })
      .catch(err => !cancelled && setQueueError(err?.response?.data?.error || err?.message || 'Failed to load queue'))
      .finally(() => !cancelled && setLoadingQueue(false));
    return () => { cancelled = true; };
  }, [hideProcessed, includeBundles, filterBrandId, filterWarehouse, isDirectMode]);

  // Direct mode (?direct=1) — resolve the brand's acquisition_date BEFORE seeding the queue,
  // then seed a single item that CARRIES it. The trust-point-queue endpoint returns
  // acquisition_date per row in normal mode; the synthesized direct-mode item must too, or
  // Effect 1's date_from floor and the pre-acquisition chart clip both no-op and the previous
  // owner's stock history renders (the chart walks back years). Seeding only after the date is
  // known also means the memory-heavy movements fetch runs once, already floored at acquisition.
  useEffect(() => {
    if (!isDirectMode) return undefined;
    let cancelled = false;
    setLoadingQueue(true);
    setQueueError(null);
    setCurrentIndex(0);
    getTrustPoints(directSku, directWarehouse, { brand_id: parseInt(directBrandId, 10) })
      .then(resp => {
        if (cancelled) return;
        const acq = resp?.success ? (resp.data?.acquisition_date || null) : null;
        setQueue([{ ...directItem, acquisition_date: acq }]);
      })
      .catch(() => {
        if (!cancelled) setQueue([{ ...directItem }]);
      })
      .finally(() => { if (!cancelled) setLoadingQueue(false); });
    return () => { cancelled = true; };
  }, [isDirectMode, directItem, directSku, directWarehouse, directBrandId]);

  const visibleQueue = queue;
  const currentItem = visibleQueue[currentIndex] || null;

  // URL-sync: jump to the URL-specified item once when the queue finishes loading.
  // Only runs when ?sku= & ?warehouse= are present and we're NOT in direct mode
  // (direct mode skips queue load entirely so this effect would no-op anyway).
  // Guard with a ref-like flag so this doesn't fight the user's navigation choices —
  // we only honor the URL hint on the initial queue load, not after filter changes.
  const [urlJumpApplied, setUrlJumpApplied] = useState(false);
  useEffect(() => {
    if (isDirectMode || urlJumpApplied || loadingQueue) return;
    if (!queue || queue.length === 0) return;
    const urlSku = searchParams.get('sku');
    const urlWh = searchParams.get('warehouse');
    if (!urlSku || !urlWh) { setUrlJumpApplied(true); return; }
    const idx = queue.findIndex(item =>
      String(item.sku).toLowerCase() === String(urlSku).toLowerCase()
      && String(item.warehouse_code).toUpperCase() === String(urlWh).toUpperCase()
    );
    if (idx >= 0) setCurrentIndex(idx);
    setUrlJumpApplied(true);
  }, [queue, loadingQueue, isDirectMode, urlJumpApplied, searchParams]);

  // URL-sync: write the current item's (sku, warehouse, brand_id) back to the URL bar
  // whenever the user navigates to a new item (Skip/Back/dropdown). Uses replace so the
  // back button still walks history, not URL revisions. Skipped in direct mode (URL is
  // already correct and rewriting it could loop).
  useEffect(() => {
    if (isDirectMode || !currentItem) return;
    const params = new URLSearchParams(searchParams);
    params.set('sku', currentItem.sku);
    params.set('warehouse', currentItem.warehouse_code);
    if (currentItem.brand_id) params.set('brand_id', String(currentItem.brand_id));
    // Only update if something actually changed — avoids history spam
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [currentItem, isDirectMode, searchParams, setSearchParams]);

  // Default-pick logic (mirrors StockBreakdownDialog.js — pick the active TP if any,
  // else fall back to most-recent physical_count, then goods_receipt, then transfer_in).
  function pickDefaultPoint(data) {
    if (!data) return null;
    const lists = {
      physical_count: data.physical_counts || [],
      goods_receipt: data.goods_receipts || [],
      transfer_in: data.transfers_in || [],
    };
    const activeType = data.active_trust_point_type;
    const activeId = data.active_trust_point_id;
    if (activeType && activeId != null) {
      const found = lists[activeType]?.find(tp => tp.id === activeId);
      if (found) return { ...found, type: activeType };
    }
    if (lists.physical_count[0]) return { ...lists.physical_count[0], type: 'physical_count' };
    if (lists.goods_receipt[0])  return { ...lists.goods_receipt[0],  type: 'goods_receipt' };
    if (lists.transfer_in[0])    return { ...lists.transfer_in[0],    type: 'transfer_in' };
    return null;
  }

  // Reset + load card data when currentItem changes
  useEffect(() => {
    setTrustPointsData(null);
    setSelectedPoint(null);
    setPreviewData(null);
    setTpError(null);
    setPreviewError(null);
    if (!currentItem) return undefined;
    // Instant path: if we prefetched this product's candidate list while the user
    // was on the previous card (or it was loaded before, for Back), use it now —
    // no spinner, no refetch.
    const cacheKey = `${currentItem.brand_id}|${currentItem.sku}|${currentItem.warehouse_code}`;
    const cached = prefetchCacheRef.current.get(cacheKey);
    if (cached) {
      setTrustPointsData(cached);
      setSelectedPoint(pickDefaultPoint(cached));
      return undefined;
    }
    let cancelled = false;
    setTpLoading(true);
    getTrustPoints(currentItem.sku, currentItem.warehouse_code, { brand_id: currentItem.brand_id })
      .then(resp => {
        if (cancelled) return;
        if (resp?.success) {
          setTrustPointsData(resp.data);
          setSelectedPoint(pickDefaultPoint(resp.data));
          prefetchCacheRef.current.set(cacheKey, resp.data); // cache for Back navigation
        } else {
          setTpError(resp?.error || 'Failed to load trust points');
        }
      })
      .catch(err => !cancelled && setTpError(err?.response?.data?.error || err?.message || 'Failed to load trust points'))
      .finally(() => !cancelled && setTpLoading(false));
    return () => { cancelled = true; };
  }, [currentItem]);

  // Prefetch the NEXT product's candidate list so the forward step is instant.
  // Only getTrustPoints (cheap); variance previews still stream on arrival, so this
  // adds no extra load on the OOM-sensitive preview path. Skipped while applying so
  // we don't compete with the apply call's backend stock-query slot.
  useEffect(() => {
    if (isDirectMode || applying) return undefined;
    const next = visibleQueue[currentIndex + 1];
    if (!next) return undefined;
    const key = `${next.brand_id}|${next.sku}|${next.warehouse_code}`;
    if (prefetchCacheRef.current.has(key)) return undefined;
    let cancelled = false;
    getTrustPoints(next.sku, next.warehouse_code, { brand_id: next.brand_id })
      .then(resp => {
        if (cancelled || !resp?.success) return;
        const cache = prefetchCacheRef.current;
        cache.set(key, resp.data);
        if (cache.size > 30) cache.delete(cache.keys().next().value); // bound memory
      })
      .catch(() => { /* non-critical — falls back to on-demand load */ });
    return () => { cancelled = true; };
  }, [currentIndex, visibleQueue, isDirectMode, applying]);

  // Per-candidate variance map: key=`${type}-${id}` → previewTrustPoint.data | null (load failed).
  // Populated by the selected-point preview below. Do not prefetch every candidate
  // on page load: each preview runs heavy stock calculations and can block the
  // visible chart/current-stock query behind speculative work.
  const [candidateVariances, setCandidateVariances] = useState({});

  // Reset candidate previews when navigating to another SKU/warehouse. The
  // selected-point preview repopulates the chosen candidate; unselected candidates
  // show "var ?" until the user selects them.
  useEffect(() => {
    setCandidateVariances({});
  }, [currentItem]);

  // Color a candidate based on |variance| / quantity. Green <5%, orange <20%, red >20%.
  // Returns null while still loading (gray dot in UI).
  const varianceColor = useCallback((variance, refQty) => {
    if (variance == null) return null;
    const absV = Math.abs(variance);
    const ref = Math.abs(parseFloat(refQty)) || 1;
    const pct = absV / ref;
    if (pct < 0.05) return { color: '#2e7d32', label: 'tight' };
    if (pct < 0.20) return { color: '#f57c00', label: 'moderate' };
    return { color: '#d32f2f', label: 'large' };
  }, []);

  // Movements chart state — full activity timeline for the current (sku, warehouse).
  // Same data source as /inventory/movements page.
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  // Bumped by the Retry button on the chart-error Alert; included in both chart
  // effect deps so a retry re-fires the movements + currentStock queries.
  const [chartRetryNonce, setChartRetryNonce] = useState(0);
  // Pending retail shipments (CIN7 wholesale orders in 'pending' status).
  // NOT deducted from calc stock — surfaced separately to render a dashed
  // "sellable" line (calc − pending) on the chart. Will eventually be replaced
  // by inventory_batches.reserved_quantity once that mechanism is validated.
  const [pendingShipments, setPendingShipments] = useState([]);
  // currentStock has TWO modes:
  //   - active mode (no selectedPoint, or selectedPoint == active TP): live system stock today,
  //     from getCalculatedStockLevels with no anchor.
  //   - projected mode (selectedPoint differs from active TP): the stock TODAY that WOULD result
  //     if selectedPoint were applied — read-only via getStockBreakdownFromTrustPoint.
  // Both endpoints are read-only — no DB writes happen on selectedPoint changes.
  const [currentStock, setCurrentStock] = useState(null);
  const [currentStockContextKey, setCurrentStockContextKey] = useState(null);
  const [stockMode, setStockMode] = useState('live'); // 'live' | 'projected'
  const stockContextKey = currentItem
    ? `${currentItem.brand_id}:${currentItem.sku}:${currentItem.warehouse_code}:${selectedPoint?.type || 'live'}:${selectedPoint?.id || ''}`
    : null;

  // Effect 1 — fetch movements when currentItem changes. CRITICAL: filter to post-acquisition
  // movements only. brands_v2.acquisition_date returned per row by the queue endpoint; without
  // this, the chart includes years of pre-acquisition history (previous owner) and walks the
  // calculated-stock line back to wildly inflated peaks.
  useEffect(() => {
    setMovements([]);
    setChartError(null);
    // Skip while applying — cancels the in-flight movements fetch so the backend
    // can give all the stock-query slots to applyTrustPoint. Re-runs after apply.
    if (!currentItem || applying) {
      if (applying) setMovementsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setMovementsLoading(true);
    (async () => {
      try {
        const params = {
          sku: currentItem.sku,
          warehouse_code: currentItem.warehouse_code,
          limit: 10000,
          offset: 0,
          include_full_history: true,
        };
        if (currentItem.acquisition_date) {
          params.date_from = currentItem.acquisition_date;
        }
        const movRes = await getDailyAggregatedMovements(currentItem.brand_id, params);
        if (!cancelled) {
          setMovements(movRes?.data?.movements || []);
          setPendingShipments(movRes?.data?.pending_shipments || []);
        }
      } catch (err) {
        if (!cancelled) setChartError(apiErrorMessage(err, 'Failed to load movements'));
      } finally {
        if (!cancelled) setMovementsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentItem, chartRetryNonce, applying]);

  // Effect 2 — fetch the stock anchored to the chosen trust point. Refires whenever the user
  // picks a different point, so the chart re-anchors. PURE READ — never writes to DB.
  useEffect(() => {
    setCurrentStock(null);
    setCurrentStockContextKey(null);
    setChartError(null);
    // Skip while applying — same reason as the movements effect: free up backend
    // stock-query slots for applyTrustPoint. Re-runs after apply completes.
    // Also wait for trust points. Without this guard, first render fetches live
    // stock with selectedPoint=null, then refetches after the default point is set.
    if (!currentItem || !trustPointsData || applying) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const activeId = trustPointsData?.active_trust_point_id;
        const activeType = trustPointsData?.active_trust_point_type;
        const isLiveAnchor = !selectedPoint
          || (selectedPoint.id === activeId && selectedPoint.type === activeType);
        if (isLiveAnchor) {
          // Live system stock today (no TP override)
          const stockRes = await getCalculatedStockLevels({
            brand_id: currentItem.brand_id,
            sku: currentItem.sku,
            warehouse_code: currentItem.warehouse_code,
          });
          if (cancelled) return;
          const items = stockRes?.data?.items || stockRes?.items || [];
          let stockQty = 0;
          for (const item of items) {
            // Match the requested SKU against the item's primary_sku OR any of its
            // child_skus. When the wizard is opened with an ALIAS (e.g. SS0000442020
            // resolving to primary SS0000442040), the API returns the item under the
            // PRIMARY sku, so a primary-only match failed -> currentStock fell to 0 ->
            // the chart anchored at 0 and the calculated-stock line ran negative.
            const skuU = currentItem.sku.toUpperCase();
            const matchesSku = item.primary_sku?.toUpperCase() === skuU
              || (item.child_skus || []).some(s => s?.toUpperCase() === skuU);
            if (matchesSku) {
              const wh = (item.warehouses || []).find(w => w.warehouse_code === currentItem.warehouse_code);
              if (wh) { stockQty = wh.quantity || wh.current_quantity || 0; break; }
              if (item.total_quantity !== undefined) { stockQty = item.total_quantity; break; }
            }
          }
          if (!cancelled) {
            setCurrentStock(stockQty);
            setCurrentStockContextKey(stockContextKey);
            setStockMode('live');
          }
        } else {
          // Projected stock today IF selectedPoint were applied (read-only)
          const breakdownRes = await getStockBreakdownFromTrustPoint(
            currentItem.sku,
            currentItem.warehouse_code,
            { date: selectedPoint.date, type: selectedPoint.type, id: selectedPoint.id },
            { brand_id: currentItem.brand_id }
          );
          if (cancelled) return;
          const projected = parseFloat(breakdownRes?.data?.quantity) || 0;
          if (!cancelled) {
            setCurrentStock(projected);
            setCurrentStockContextKey(stockContextKey);
            setStockMode('projected');
          }
        }
      } catch (err) {
        if (!cancelled) setChartError(err?.response?.data?.error || err?.message || 'Failed to compute current stock');
      }
    })();
    return () => { cancelled = true; };
  }, [currentItem, selectedPoint, trustPointsData, chartRetryNonce, applying, stockContextKey]);

  // Derived: the calculated-stock line for the chart.
  //
  // ALWAYS anchored to `currentStock` (the value in the header) and walked BACKWARD over
  // the movement ledger — the same approach VIEW A "Stock Level History" uses. This makes
  // the line's right edge EQUAL the header BY CONSTRUCTION, in both header modes:
  //   - live mode:      currentStock = the stock engine (getCalculatedStockLevels) — the
  //                     single number MSR / Stock Levels / everything else uses.
  //   - projected mode: currentStock = "stock today IF this TP were applied"
  //                     (getStockBreakdownFromTrustPoint); the whole line shifts to that
  //                     level and the header relabels to "Projected stock if applied".
  //
  // We deliberately no longer FORWARD-walk from a trust point's raw count. That made the
  // line a SECOND, independently-computed number that could disagree with the header
  // (different ShipHero windowing / anchor than the engine) — the confusing "935 in the
  // header, ~180 on the line" split. A pure back-walk still satisfies the property the user
  // expects — switching trust points shifts the SAME shape vertically, because changing
  // currentStock moves every point by the same delta — and never re-anchors the balance to
  // a count (the rule set for VIEW A, Vishal 2026-06-02): stock_count rows stay
  // observations, not deltas. (movements arrive newest-first / DESC.)
  const balances = (() => {
    if (currentStock == null || !movements || movements.length === 0) return [];
    const out = [];
    let bal = currentStock;
    for (const m of movements) {
      out.push(bal);
      if (m.movement_type !== 'stock_count_report') bal -= (parseFloat(m.total_quantity) || 0);
    }
    return out;
  })();
  // The chart line anchors on currentStock (not previewData), so it can paint as soon as
  // movements + currentStock are ready — no need to wait for the heavier, advisory
  // previewTrustPoint call. currentStock already reflects the selected mode (live vs
  // projected), so switching trust points re-anchors the line when the new currentStock
  // lands, with no intermediate raw-count frame to re-paint.
  const chartLoading = movementsLoading
    || (currentItem && currentStock == null && !chartError);
  const movementsData = movements.length > 0 && currentStock != null
    ? { movements, balances, currentStock, stockMode }
    : null;

  // Refetch preview whenever selectedPoint changes
  useEffect(() => {
    if (!currentItem || !selectedPoint) { setPreviewData(null); return undefined; }
    // Skip while applying — preview uses the same heavy stock query as apply.
    // Cancelling lets apply have all the backend's concurrency slots.
    if (applying) { setPreviewLoading(false); return undefined; }
    // Live/projected stock is on the chart's critical path. Preview is advisory
    // and can safely wait; otherwise its stock calculations occupy the shared
    // heavy-query slots and make the visible page wait behind speculative work.
    if (currentStockContextKey !== stockContextKey) {
      setPreviewData(null);
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    const payload = {
      sku: currentItem.sku,
      warehouse_code: currentItem.warehouse_code,
      brand_id: currentItem.brand_id,
      trust_point_type: selectedPoint.type,
      trust_point_id: selectedPoint.id,
      trust_point_date: selectedPoint.date,
      reason_prefix: `manual_wizard:${currentItem.sku}@${currentItem.warehouse_code}:${selectedPoint.type}:${selectedPoint.id}`,
    };
    previewTrustPoint(payload)
      .then(resp => {
        if (cancelled) return;
        const key = `${selectedPoint.type}-${selectedPoint.id}`;
        if (resp?.success) {
          setPreviewData(resp.data);
          setCandidateVariances(prev => ({ ...prev, [key]: resp.data }));
        } else {
          setCandidateVariances(prev => ({ ...prev, [key]: null }));
          setPreviewError(resp?.error || 'Preview failed');
        }
      })
      .catch(err => {
        if (cancelled) return;
        setCandidateVariances(prev => ({
          ...prev,
          [`${selectedPoint.type}-${selectedPoint.id}`]: null,
        }));
        setPreviewError(err?.response?.data?.error || err?.message || 'Preview failed');
      })
      .finally(() => !cancelled && setPreviewLoading(false));
    return () => { cancelled = true; };
  }, [currentItem, selectedPoint, applying, currentStockContextKey, stockContextKey]);

  // Build the apply payload (same shape as preview, with reason_prefix tagging for rollback).
  // No client-side math: applyTrustPoint computes variance server-side.
  const buildApplyPayload = useCallback(() => {
    if (!currentItem || !selectedPoint) return null;
    return {
      sku: currentItem.sku,
      warehouse_code: currentItem.warehouse_code,
      brand_id: currentItem.brand_id,
      trust_point_type: selectedPoint.type,
      trust_point_id: selectedPoint.id,
      trust_point_date: selectedPoint.date,
      reason_prefix: `manual_wizard:${currentItem.sku}@${currentItem.warehouse_code}:${selectedPoint.type}:${selectedPoint.id}`,
    };
  }, [currentItem, selectedPoint]);

  const handleApplyAndNext = useCallback(async () => {
    const payload = buildApplyPayload();
    // previewData is no longer required — the backend computes variance independently
    // and bails to no-op when |variance| < 0.01, so applying without a successful
    // preview is safe. If preview failed but apply succeeds, we still get full server
    // validation via verifyTrustPointId + the variance recompute.
    if (!payload) return;
    if (!backupStatus?.success) {
      setApplyError('Backup inventory_adjustments first.');
      return;
    }
    setApplying(true);
    setApplyError(null);
    try {
      const resp = await applyTrustPoint(payload);
      if (resp?.success) {
        // Optimistically mark the row processed; queue will refresh on next hideProcessed toggle
        setQueue(q => q.map((row, i) =>
          i === currentIndex ? { ...row, already_has_trust_point: true } : row
        ));
        if (isDirectMode) setDirectDone(true);
        else setCurrentIndex(i => i + 1);
      } else {
        setApplyError(resp?.error || 'Apply failed');
      }
    } catch (err) {
      setApplyError(err?.response?.data?.error || err?.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [buildApplyPayload, backupStatus, currentIndex, isDirectMode]);

  // Accept the existing active trust point as-is. No stock change — inserts a marker row
  // tagged `manual_wizard:accepted` so the row counts as processed for the hide filter.
  // "Accept Current & Next" has two modes, dispatched by whether a TP has been applied:
  //   - applied_trust_point_id != null  → insert a zero-variance "accepted" marker
  //     (no stock change, just dismisses the row from the review queue).
  //   - applied_trust_point_id == null  → apply the auto-default TP highlighted in the
  //     purple badge (calls applyTrustPoint with the active TP). Same effect as clicking
  //     Apply & Next without changing the pre-filled selection — but explicitly bound to
  //     the auto-default (ignores manual selectedPoint changes), so the button's promise
  //     ("accept the currently highlighted one") holds either way.
  const handleAcceptCurrentAndNext = useCallback(async () => {
    if (!currentItem) return;

    // Path A — accept an already-applied TP (no stock change).
    if (trustPointsData?.applied_trust_point_id != null) {
      setApplying(true);
      setApplyError(null);
      try {
        const resp = await acceptCurrentTrustPoint({
          sku: currentItem.sku,
          warehouse_code: currentItem.warehouse_code,
          brand_id: currentItem.brand_id,
        });
        if (resp?.success) {
          setQueue(q => q.map((row, i) =>
            i === currentIndex ? { ...row, already_has_trust_point: true } : row
          ));
          if (isDirectMode) setDirectDone(true);
          else setCurrentIndex(i => i + 1);
        } else {
          setApplyError(resp?.error || 'Accept failed');
        }
      } catch (err) {
        setApplyError(err?.response?.data?.error || err?.message || 'Accept failed');
      } finally {
        setApplying(false);
      }
      return;
    }

    // Path B — nothing applied yet: apply the auto-default (the purple-badged TP).
    const activeId = trustPointsData?.active_trust_point_id;
    const activeType = trustPointsData?.active_trust_point_type;
    if (activeId == null || !activeType) {
      setApplyError('No default trust point available to accept.');
      return;
    }
    const lists = {
      physical_count: trustPointsData.physical_counts || [],
      goods_receipt: trustPointsData.goods_receipts || [],
      transfer_in: trustPointsData.transfers_in || [],
    };
    const activeTP = lists[activeType]?.find(tp => tp.id === activeId);
    if (!activeTP) {
      setApplyError('Default trust point not found in candidate lists.');
      return;
    }
    if (!backupStatus?.success) {
      setApplyError('Backup inventory_adjustments first.');
      return;
    }
    setApplying(true);
    setApplyError(null);
    try {
      const resp = await applyTrustPoint({
        sku: currentItem.sku,
        warehouse_code: currentItem.warehouse_code,
        brand_id: currentItem.brand_id,
        trust_point_type: activeType,
        trust_point_id: activeTP.id,
        trust_point_date: activeTP.date,
        reason_prefix: `manual_wizard:accept-default:${currentItem.sku}@${currentItem.warehouse_code}:${activeType}:${activeTP.id}`,
      });
      if (resp?.success) {
        setQueue(q => q.map((row, i) =>
          i === currentIndex ? { ...row, already_has_trust_point: true } : row
        ));
        if (isDirectMode) setDirectDone(true);
        else setCurrentIndex(i => i + 1);
      } else {
        setApplyError(resp?.error || 'Apply failed');
      }
    } catch (err) {
      setApplyError(err?.response?.data?.error || err?.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [currentItem, trustPointsData, currentIndex, backupStatus, isDirectMode]);

  // Keyboard shortcuts: Enter=Apply&Next, S=Skip, B=Back, ←/→=cycle selectedPoint by date
  useEffect(() => {
    const handleKey = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target?.tagName)) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        handleApplyAndNext();
      } else if ((e.key === 's' || e.key === 'S') && !isDirectMode) {
        setCurrentIndex(i => i + 1);
      } else if ((e.key === 'b' || e.key === 'B') && !isDirectMode) {
        setCurrentIndex(i => Math.max(0, i - 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (!trustPointsData) return;
        const all = [
          ...(trustPointsData.physical_counts || []).map(p => ({ ...p, type: 'physical_count' })),
          ...(trustPointsData.goods_receipts  || []).map(p => ({ ...p, type: 'goods_receipt'  })),
          ...(trustPointsData.transfers_in    || []).map(p => ({ ...p, type: 'transfer_in'    })),
        ].sort((a, b) => String(a.date).localeCompare(String(b.date)));
        if (!all.length) return;
        const curIdx = selectedPoint
          ? all.findIndex(p => p.type === selectedPoint.type && p.id === selectedPoint.id)
          : -1;
        const next = e.key === 'ArrowRight'
          ? all[Math.min(all.length - 1, curIdx + 1)]
          : all[Math.max(0, curIdx - 1)];
        if (next) setSelectedPoint(next);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleApplyAndNext, trustPointsData, selectedPoint, isDirectMode]);

  const handleBackup = useCallback(async () => {
    setBackupBusy(true);
    try {
      const resp = await backupAdjustments();
      if (resp?.success) setBackupStatus(resp);
      else setBackupStatus({ error: resp?.error || 'Backup failed' });
    } catch (err) {
      setBackupStatus({ error: err?.response?.data?.error || err?.message || 'Backup failed' });
    } finally {
      setBackupBusy(false);
    }
  }, []);

  // Auto-run today's backup on mount. Idempotent: if the daily snapshot already exists
  // the backend returns {already_exists: true} cheaply. So multiple wizard loads in the
  // same day cost ~1 quick API call each. User never needs to click the orange button
  // unless this fails (in which case they can retry it manually).
  useEffect(() => {
    if (backupStatus) return undefined;
    handleBackup();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggleHide = (e) => {
    const v = e.target.checked;
    setHideProcessed(v);
    localStorage.setItem('tpw.hideProcessed', String(v));
    setCurrentIndex(0);
  };

  if (loadingQueue) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (queueError) {
    return (
      <Box p={4}>
        <Alert severity="error">{queueError}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Stack direction="column" spacing={0.5}>
          <Typography variant="h5">
            {isDirectMode ? 'Allocate Trust Point' : 'Trust Point Wizard'}
          </Typography>
          {isDirectMode && (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => navigate(
                `/inventory/movements?sku=${encodeURIComponent(directSku)}` +
                `&warehouse=${encodeURIComponent(directWarehouse)}` +
                (directBrandId ? `&brand=${encodeURIComponent(directBrandId)}` : '')
              )}
              sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 13 }}
            >
              <ArrowBackIcon fontSize="inherit" sx={{ mr: 0.5 }} />
              Back to movements
            </Link>
          )}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          {!isDirectMode && (
            <>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Brand</InputLabel>
                <Select
                  label="Brand"
                  value={filterBrandId ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const next = v === '' ? null : Number(v);
                    setFilterBrandId(next);
                    if (next == null) localStorage.removeItem('tpw.filterBrandId');
                    else localStorage.setItem('tpw.filterBrandId', String(next));
                  }}
                >
                  <MenuItem value=""><em>All brands</em></MenuItem>
                  {allBrandOptions.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Warehouse</InputLabel>
                <Select
                  label="Warehouse"
                  value={filterWarehouse ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const next = v === '' ? null : v;
                    setFilterWarehouse(next);
                    if (next == null) localStorage.removeItem('tpw.filterWarehouse');
                    else localStorage.setItem('tpw.filterWarehouse', next);
                  }}
                >
                  <MenuItem value=""><em>All warehouses</em></MenuItem>
                  {allWarehouseOptions.map(wh => (
                    <MenuItem key={wh} value={wh}>{wh}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch checked={hideProcessed} onChange={onToggleHide} />}
                label="Hide already-processed"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={includeBundles}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setIncludeBundles(v);
                      localStorage.setItem('tpw.includeBundles', String(v));
                      setCurrentIndex(0);
                    }}
                  />
                }
                label="Include bundles"
              />
            </>
          )}
          {backupStatus?.success ? (
            <Chip
              color="success"
              label={`✓ Backup: ${backupStatus.table_name} (${backupStatus.row_count} rows)`}
            />
          ) : backupStatus?.error ? (
            <Chip color="error" label={`Backup failed: ${backupStatus.error}`} />
          ) : (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleBackup}
              disabled={backupBusy}
            >
              {backupBusy ? 'Backing up…' : '⚠ Backup inventory_adjustments now'}
            </Button>
          )}
        </Stack>
      </Stack>

      {isDirectMode && directDone ? (
        <Paper sx={{ p: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6">Trust point allocated</Typography>
              <Typography variant="body2" color="text.secondary">
                {directSku} @ {directWarehouse} — adjustments written to <code>inventory_adjustments</code>.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(
                `/inventory/movements?sku=${encodeURIComponent(directSku)}` +
                `&warehouse=${encodeURIComponent(directWarehouse)}` +
                (directBrandId ? `&brand=${encodeURIComponent(directBrandId)}` : '')
              )}
            >
              Back to movements
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setDirectDone(false); }}
            >
              Pick another trust point
            </Button>
          </Stack>
        </Paper>
      ) : visibleQueue.length === 0 ? (
        <Alert severity="info">
          No (sku, warehouse) pairs need review. Try toggling "Hide already-processed".
        </Alert>
      ) : !currentItem ? (
        <Alert severity="success">
          All done — you've reached the end of the queue ({visibleQueue.length} reviewed).
        </Alert>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
              {currentItem.image_url ? (
                <Box
                  component="img"
                  src={currentItem.image_url}
                  alt={currentItem.product_name || currentItem.sku}
                  sx={{
                    width: 64, height: 64, borderRadius: 1,
                    objectFit: 'cover', border: '1px solid #eee', flexShrink: 0,
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : null}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  {currentItem.product_name || currentItem.sku}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {currentItem.sku} @ {currentItem.warehouse_code}
                  {currentItem.category ? ` · ${currentItem.category}` : ''}
                  {currentItem.is_bundle ? ' · 📦 bundle' : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Brand {currentItem.brand_name || allBrandOptions.find(b => b.id === currentItem.brand_id)?.name || `#${currentItem.brand_id}`}
                  {currentItem.count_count != null && (
                    <> · {currentItem.count_count} counts · {currentItem.first_count_date} → {currentItem.last_count_date}</>
                  )}
                  {currentItem.daily_velocity > 0 && (
                    <>
                      {' · '}
                      <span style={{ color: '#1976d2', fontWeight: 500 }}>
                        {Number(currentItem.daily_velocity).toFixed(1)} units/day
                      </span>
                      {' '}({currentItem.units_shipped_90d} in 90d)
                    </>
                  )}
                  {currentItem.acquisition_date && (
                    <>
                      {' · '}
                      <span style={{ color: '#7b1fa2' }}>
                        acquired {currentItem.acquisition_date}
                      </span>
                    </>
                  )}
                  {currentItem.already_has_trust_point && ' · ✓ wizard-processed'}
                </Typography>
              </Box>
            </Stack>
            {isDirectMode ? (
              <Chip size="small" variant="outlined" label="Direct allocation" sx={{ ml: 1 }} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {currentIndex + 1} / {visibleQueue.length}
              </Typography>
            )}
          </Stack>

          {tpLoading ? (
            <Box sx={{ minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : tpError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{tpError}</Alert>
          ) : !trustPointsData ? null : (
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              {/* Inline movements chart — same logic as /inventory/movements LineChart */}
              <Box sx={{ flex: 2, minHeight: 360 }}>
                {applying ? (
                  <Box sx={{ minHeight: 360, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap: 2 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary">
                      Applying trust point… chart paused to free backend capacity.
                    </Typography>
                  </Box>
                ) : chartLoading ? (
                  <Box sx={{ minHeight: 360, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : chartError ? (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => setChartRetryNonce(n => n + 1)}
                      >
                        Retry
                      </Button>
                    }
                  >
                    {chartError}
                  </Alert>
                ) : !movementsData || movementsData.movements.length === 0 ? (
                  <Alert severity="info">No movement data for this (sku, warehouse).</Alert>
                ) : (() => {
                  const { movements, balances, currentStock } = movementsData;
                  // Pending pre-aggregation: for each movement date D, the cumulative
                  // qty of retail orders in 'pending' status whose event_date ≤ D.
                  // sellableBalance = calculatedBalance − pendingAtDate.
                  const pendingByMovementIdx = (() => {
                    if (!pendingShipments || pendingShipments.length === 0) {
                      return movements.map(() => 0);
                    }
                    // Sort pending by date once
                    const sortedPending = [...pendingShipments]
                      .map(p => ({ date: String(p.date).slice(0, 10), qty: Number(p.quantity) || 0 }))
                      .sort((a, b) => a.date.localeCompare(b.date));
                    return movements.map((m) => {
                      const mDate = String(m.movement_date).slice(0, 10);
                      let sum = 0;
                      for (const p of sortedPending) {
                        if (p.date <= mDate) sum += p.qty;
                        else break;
                      }
                      return sum;
                    });
                  })();
                  // Build chart data — reverse so chronological (oldest left, newest right)
                  const raw = movements.map((m, idx) => {
                    const dateObj = new Date(m.movement_date);
                    const isStockCount = m.movement_type === 'stock_count_report';
                    const calcBal = balances[idx];
                    const pendingHere = pendingByMovementIdx[idx] || 0;
                    return {
                      dateObj,
                      fullDate: m.movement_date,
                      year: dateObj.getFullYear(),
                      balance: isStockCount ? null : calcBal,
                      calculatedBalance: calcBal,
                      sellableBalance: (isStockCount || calcBal == null || pendingHere === 0)
                        ? null
                        : (calcBal - pendingHere),
                      pendingAtDate: pendingHere,
                      reportedStock: isStockCount ? m.total_quantity : null,
                      quantity: m.total_quantity,
                      type: m.movement_type,
                      isStockCount,
                      isAdjustment: ['adjustment', 'physical_count', 'adjustment_increase', 'adjustment_decrease', 'shiphero_adjustment'].includes(m.movement_type),
                      isShipheroAdjustment: m.movement_type === 'shiphero_adjustment',
                    };
                  }).reverse();
                  // Clip the rendered series at the brand's acquisition date: stock before we
                  // owned the brand is the previous owner's history and must not show. balances[]
                  // above is walked back over the FULL ledger from currentStock (the header value),
                  // so the first visible point keeps its correct balance — the line starts AT
                  // acquisition at the real opening stock, never at 0. This removes the pre-
                  // acquisition stock_count_report marker that bypasses the backend date_from
                  // cutoff. Brands with no acquisition_date are unaffected (no-op).
                  const acqFloor = currentItem && currentItem.acquisition_date
                    ? String(currentItem.acquisition_date).slice(0, 10)
                    : null;
                  // Always include year on every label so a date like "Apr 16" isn't ambiguous
                  // when the chart spans multiple years.
                  const chartData = raw
                    .filter(item => !acqFloor || !item.fullDate
                      || String(item.fullDate).slice(0, 10) >= acqFloor)
                    .map(item => ({
                      ...item,
                      date: item.dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }),
                    }));
                  const shipheroAuditRows = movements
                    .map((m, idx) => ({
                      ...m,
                      balanceAfter: balances[idx],
                      displayDate: new Date(m.movement_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                    }))
                    .filter(m => m.movement_type === 'shiphero_adjustment');
                  const labelByFullDate = new Map();
                  chartData.forEach(d => labelByFullDate.set(d.fullDate, d.date));
                  const tpFullDate = selectedPoint
                    ? chartData.find(d => d.fullDate && String(d.fullDate).startsWith(selectedPoint.date))?.fullDate
                    : null;
                  // Resolve the active/current trust point's chart anchor (independent of selectedPoint)
                  // so we can render a second vertical reference line for it.
                  const activeTpDate = (() => {
                    const aId = trustPointsData?.active_trust_point_id;
                    const aType = trustPointsData?.active_trust_point_type;
                    if (aId == null || !aType) return null;
                    const lists = {
                      physical_count: trustPointsData.physical_counts || [],
                      goods_receipt:  trustPointsData.goods_receipts  || [],
                      transfer_in:    trustPointsData.transfers_in    || [],
                    };
                    const found = lists[aType]?.find(tp => tp.id === aId);
                    return found?.date || null;
                  })();
                  const activeTpFullDate = activeTpDate
                    ? chartData.find(d => d.fullDate && String(d.fullDate).startsWith(activeTpDate))?.fullDate
                    : null;
                  // Don't double-draw the line if active and selected are the same point.
                  const drawActiveLine = activeTpFullDate && activeTpFullDate !== tpFullDate;
                  return (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {movementsData.stockMode === 'projected' ? (
                          <>
                            <span style={{ color: '#7b1fa2', fontWeight: 600 }}>
                              Projected stock if applied: {formatNumber ? formatNumber(currentStock) : currentStock}
                            </span>
                            {' · selected TP: '}{selectedPoint.date}
                            {' · '}
                            <span style={{ fontStyle: 'italic' }}>not yet written — Apply to commit</span>
                          </>
                        ) : (
                          <>
                            Current stock (live): {formatNumber ? formatNumber(currentStock) : currentStock}
                            {selectedPoint && ` · selected TP: ${selectedPoint.date}`}
                          </>
                        )}
                        {(() => {
                          const totalPending = (pendingShipments || []).reduce((s, p) => s + (Number(p.quantity) || 0), 0);
                          if (totalPending <= 0) return null;
                          const sellable = currentStock != null ? (currentStock - totalPending) : null;
                          return (
                            <>
                              {' · '}
                              <span style={{ color: '#ef6c00', fontWeight: 600 }} title="calc stock − pending wholesale orders not yet shipped">
                                Sellable: {sellable != null && formatNumber ? formatNumber(sellable) : sellable}
                              </span>
                              {' · '}
                              <span style={{ color: '#ef6c00' }} title={pendingShipments.map(p => `${p.date}: ${p.quantity} (${p.customer_name || p.order_id})`).join('\n')}>
                                Pending: {formatNumber ? formatNumber(totalPending) : totalPending} units in {pendingShipments.length} orders
                              </span>
                            </>
                          );
                        })()}
                      </Typography>
                      <Box sx={{ width:'100%', height: 380 }}>
                        <ResponsiveContainer>
                          <LineChart data={chartData} margin={{ top:20, right:30, left:20, bottom:20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis
                              dataKey="fullDate"
                              tick={{ fontSize: 11 }}
                              tickMargin={10}
                              tickFormatter={(v) => labelByFullDate.get(v) || v}
                            />
                            <YAxis
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v) => formatNumber ? formatNumber(v) : v}
                              domain={['auto', 'auto']}
                            />
                            <RechartsTooltip
                              content={({ active, payload }) => {
                                if (!active || !payload || payload.length === 0) return null;
                                const d = payload[0].payload;
                                const lab = d.dateObj.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
                                const fmt = (v) => v == null || Number.isNaN(v) ? '—' : (formatNumber ? formatNumber(v) : v);
                                const wrap = { background:'#fff', border:'1px solid #ccc', padding:'8px 10px', fontSize:12, lineHeight:1.5 };
                                const labelStyle = { color:'#666', marginRight:6 };
                                if (d.isStockCount) {
                                  const reported = d.reportedStock;
                                  const calc = d.calculatedBalance;
                                  const variance = (reported != null && calc != null) ? (reported - calc) : null;
                                  const vColor = variance == null ? '#666' : (variance > 0 ? '#2e7d32' : variance < 0 ? '#d32f2f' : '#666');
                                  return (
                                    <div style={wrap}>
                                      <div style={{ fontWeight:600, marginBottom:4 }}>{lab} · Stock count</div>
                                      <div><span style={labelStyle}>Warehouse reported:</span><b>{fmt(reported)}</b></div>
                                      <div><span style={labelStyle}>Calculated (system):</span><b>{fmt(calc)}</b></div>
                                      <div><span style={labelStyle}>Variance:</span>
                                        <b style={{ color: vColor }}>{variance == null ? '—' : `${variance > 0 ? '+' : ''}${fmt(variance)}`}</b>
                                      </div>
                                    </div>
                                  );
                                }
                                const qty = d.quantity;
                                const qColor = qty > 0 ? '#2e7d32' : qty < 0 ? '#d32f2f' : '#666';
                                return (
                                  <div style={wrap}>
                                    <div style={{ fontWeight:600, marginBottom:4 }}>
                                      {lab} · {d.type === 'shiphero_adjustment' ? 'ShipHero adjustment' : d.type}
                                    </div>
                                    <div><span style={labelStyle}>Movement:</span>
                                      <b style={{ color: qColor }}>{qty >= 0 ? '+' : ''}{fmt(qty)}</b>
                                    </div>
                                    {d.isShipheroAdjustment && (
                                      <div><span style={labelStyle}>Source:</span><b>ShipHero cycle count</b></div>
                                    )}
                                    <div><span style={labelStyle}>Stock after:</span><b>{fmt(d.calculatedBalance)}</b></div>
                                  </div>
                                );
                              }}
                            />
                            {drawActiveLine && (
                              <ReferenceLine
                                x={activeTpFullDate}
                                stroke="#9c27b0"
                                strokeDasharray="4 2"
                                strokeWidth={2}
                                label={{ value: `★ Current TP · ${activeTpDate}`, position: 'insideTopLeft', fill:'#7b1fa2', fontSize: 11, fontWeight: 600 }}
                              />
                            )}
                            {tpFullDate && (
                              <ReferenceLine
                                x={tpFullDate}
                                stroke="#16a34a"
                                strokeDasharray="6 3"
                                strokeWidth={2}
                                label={{ value: `Selected · ${selectedPoint.date}`, position: 'insideTopRight', fill:'#16a34a', fontSize: 11, fontWeight: 600 }}
                              />
                            )}
                            <Line
                              type="stepAfter"
                              dataKey="balance"
                              stroke="#1976d2"
                              strokeWidth={2}
                              connectNulls
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload.isStockCount || !cx || !cy) return null;
                                if (payload.isShipheroAdjustment) {
                                  return <circle key={`shiphero-${cx}-${cy}`} cx={cx} cy={cy} r={6} fill="#00897b" stroke="#00695c" strokeWidth={2} />;
                                }
                                if (payload.isAdjustment) {
                                  return <circle key={`adj-${cx}-${cy}`} cx={cx} cy={cy} r={6} fill="#ff9800" stroke="#f57c00" strokeWidth={2} />;
                                }
                                const color = payload.quantity > 0 ? '#4caf50' : '#f44336';
                                return <circle key={`mov-${cx}-${cy}`} cx={cx} cy={cy} r={4} fill={color} />;
                              }}
                              activeDot={{ r: 6, strokeWidth: 2 }}
                            />
                            {/* Sellable stock = calc − pending wholesale. Only renders when pending > 0. */}
                            <Line
                              type="stepAfter"
                              dataKey="sellableBalance"
                              stroke="#ef6c00"
                              strokeWidth={1.5}
                              strokeDasharray="6 3"
                              connectNulls
                              dot={false}
                              activeDot={false}
                              isAnimationActive={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="reportedStock"
                              stroke="none"
                              strokeWidth={0}
                              connectNulls={false}
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (!payload.isStockCount || !cx || !cy) return null;
                                const match = (trustPointsData?.physical_counts || []).find(
                                  pc => pc.date && payload.fullDate && String(payload.fullDate).startsWith(pc.date)
                                );
                                const matched = match ? { ...match, type: 'physical_count' } : null;
                                const isSel = matched && selectedPoint?.type === 'physical_count' && selectedPoint?.id === matched.id;
                                // Color by variance magnitude (loaded incrementally). Default purple = unknown/loading.
                                const v = matched ? candidateVariances[`physical_count-${matched.id}`] : null;
                                const vc = v && varianceColor(v.variance, matched.quantity);
                                const fill = isSel ? '#d32f2f' : (vc?.color || '#9c27b0');
                                const stroke = isSel ? '#b71c1c' : (vc?.color === '#2e7d32' ? '#1b5e20' : vc?.color === '#f57c00' ? '#e65100' : vc?.color === '#d32f2f' ? '#b71c1c' : '#7b1fa2');
                                return (
                                  <polygon
                                    key={`cnt-${cx}-${cy}`}
                                    points={`${cx},${cy-8} ${cx+8},${cy} ${cx},${cy+8} ${cx-8},${cy}`}
                                    fill={fill}
                                    stroke={stroke}
                                    strokeWidth={isSel ? 3 : 2}
                                    style={{ cursor: matched ? 'pointer' : 'default' }}
                                    onClick={() => { if (matched) setSelectedPoint(matched); }}
                                  />
                                );
                              }}
                              activeDot={{ r: 8, strokeWidth: 2, fill:'#9c27b0', stroke:'#7b1fa2' }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                      <Box sx={{ display:'flex', flexWrap:'wrap', gap: 2, mt: 1, fontSize: 11, color: 'text.secondary', justifyContent: 'center' }}>
                        <span>● blue line = calculated stock</span>
                        <span>
                          ◆ count by variance:
                          <span style={{ color: '#2e7d32', marginLeft: 4 }}>tight (&lt;5%)</span>
                          <span style={{ color: '#f57c00', marginLeft: 4 }}>moderate (&lt;20%)</span>
                          <span style={{ color: '#d32f2f', marginLeft: 4 }}>large</span>
                          <span style={{ color: '#9c27b0', marginLeft: 4 }}>loading</span>
                        </span>
                        <span>● orange = adjustment</span>
                        <span style={{ color: '#00897b' }}>● teal = ShipHero cycle count</span>
                        <span>● green = inflow</span>
                        <span>● red = outflow</span>
                        <span style={{ color: '#ef6c00' }}>┄ dashed orange = sellable (calc − pending wholesale)</span>
                      </Box>
                      {shipheroAuditRows.length > 0 && (
                        <Box sx={{ mt: 2, border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ px: 1.5, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                            <Box>
                              <Typography variant="subtitle2">ShipHero adjustments used</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Included when mapped ShipHero rows are manual cycle count amendments.{' '}
                                <Link href="/inventory/mappings?tab=shiphero" underline="hover">Manage ShipHero mappings</Link>
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              color="success"
                              label={`${formatNumber ? formatNumber(shipheroAuditRows.reduce((sum, r) => sum + (Number(r.total_quantity) || 0), 0)) : shipheroAuditRows.reduce((sum, r) => sum + (Number(r.total_quantity) || 0), 0)} units`}
                            />
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 1fr) minmax(80px, 0.7fr) minmax(80px, 0.7fr) minmax(100px, 0.8fr)', px: 1.5, py: 0.75, bgcolor: '#f9fafb', color: 'text.secondary', fontSize: 12, fontWeight: 600 }}>
                            <Box>Date</Box>
                            <Box>Units</Box>
                            <Box>Events</Box>
                            <Box>Stock after</Box>
                          </Box>
                          {shipheroAuditRows.map((row, idx) => {
                            const qty = Number(row.total_quantity) || 0;
                            const qtyColor = qty > 0 ? '#2e7d32' : qty < 0 ? '#d32f2f' : '#666';
                            return (
                              <Box
                                key={`${row.movement_date}-${idx}`}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'minmax(110px, 1fr) minmax(80px, 0.7fr) minmax(80px, 0.7fr) minmax(100px, 0.8fr)',
                                  px: 1.5,
                                  py: 0.75,
                                  borderTop: idx === 0 ? 0 : '1px solid #eef2f7',
                                  fontSize: 13,
                                  alignItems: 'center',
                                }}
                              >
                                <Box>{row.displayDate}</Box>
                                <Box sx={{ color: qtyColor, fontWeight: 600 }}>{qty >= 0 ? '+' : ''}{formatNumber ? formatNumber(qty) : qty}</Box>
                                <Box>{formatNumber ? formatNumber(row.order_count || 1) : (row.order_count || 1)}</Box>
                                <Box>{row.balanceAfter == null ? '—' : (formatNumber ? formatNumber(row.balanceAfter) : row.balanceAfter)}</Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Box>

              {/* Preview side panel — current TP pinned on top, then candidates list */}
              <Box sx={{ flex: 1, p: 2, border: '1px solid #eee', borderRadius: 1, maxHeight: 720, overflow: 'auto' }}>
                {(() => {
                  const activeId = trustPointsData.active_trust_point_id;
                  const activeType = trustPointsData.active_trust_point_type;
                  // isApplied: this id is actually persisted in inventory_adjustments (so
                  // Accept Current is a valid action). Otherwise activeId is just a suggested
                  // default — applying it requires "Apply & Next", not "Accept".
                  const isApplied = trustPointsData.applied_trust_point_id != null
                    && trustPointsData.applied_trust_point_id === activeId;
                  const lists = {
                    physical_count: { label: 'Counts',    items: trustPointsData.physical_counts || [], color: '#1976d2' },
                    goods_receipt:  { label: 'Receipts',  items: trustPointsData.goods_receipts  || [], color: '#2e7d32' },
                    transfer_in:    { label: 'Transfers', items: trustPointsData.transfers_in    || [], color: '#ed6c02' },
                  };
                  const activeTP = activeId != null && activeType ? lists[activeType]?.items.find(tp => tp.id === activeId) : null;
                  return (
                    <>
                      {activeTP ? (
                        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: '#f3e5f5', border: '2px solid #9c27b0' }}>
                          <Typography variant="caption" sx={{ color: '#7b1fa2', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            ★ CURRENT TRUST POINT {isApplied ? '(applied)' : '(auto-selected — Accept Current will store it)'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {activeTP.date} · qty {activeTP.quantity}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lists[activeType].label} · id {activeTP.id}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: '#fff3e0', border: '1px dashed #f57c00' }}>
                          <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600 }}>
                            No trust point candidates — nothing to anchor against.
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="subtitle2" gutterBottom>
                        {activeTP ? 'Change to a different point:' : 'Pick a trust point:'}
                      </Typography>
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        {Object.entries(lists).flatMap(([type, list]) => list.items.map(tp => {
                          const isActive = activeType === type && activeId === tp.id;
                          const isSelected = selectedPoint?.type === type && selectedPoint?.id === tp.id;
                          // Variance from incremental batch fetch — null while loading.
                          const v = candidateVariances[`${type}-${tp.id}`];
                          const variance = v?.variance;
                          const vc = v ? varianceColor(variance, tp.quantity) : null;
                          return (
                            <Box
                              key={`${type}-${tp.id}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedPoint({ ...tp, type })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedPoint({ ...tp, type });
                                }
                              }}
                              sx={{
                                p: 1.5,
                                minHeight: 56,
                                borderRadius: 1,
                                cursor: 'pointer',
                                border: isSelected ? '2px solid #d32f2f' : isActive ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                                bgcolor: isSelected ? '#fff5f5' : isActive ? '#f3e5f5' : '#fafafa',
                                transition: 'background-color 0.12s, border-color 0.12s',
                                '&:hover': { bgcolor: isSelected ? '#fff5f5' : isActive ? '#f3e5f5' : '#eef2f7', borderColor: isSelected ? '#d32f2f' : '#90a4ae' },
                                display: 'flex', alignItems: 'center', gap: 1,
                                fontSize: 13,
                              }}
                            >
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: vc?.color || list.color, flexShrink: 0 }} />
                              <Box sx={{ flex: 1 }}>
                                <Box>
                                  {tp.date} · qty {tp.quantity}
                                  {variance != null ? (
                                    <span style={{
                                      marginLeft: 6,
                                      color: vc?.color || '#666',
                                      fontWeight: 600,
                                      fontSize: 12,
                                    }}>
                                      · var {variance > 0 ? '+' : ''}{variance}
                                    </span>
                                  ) : v === null && Object.prototype.hasOwnProperty.call(candidateVariances, `${type}-${tp.id}`) ? (
                                    <span style={{ marginLeft: 6, color: '#999', fontSize: 11 }}>· var ?</span>
                                  ) : (
                                    <span style={{ marginLeft: 6, color: '#bbb', fontSize: 11 }}>· loading…</span>
                                  )}
                                </Box>
                                <Box sx={{ fontSize: 11, color: 'text.secondary' }}>
                                  {list.label}
                                  {isActive && ` · ${isApplied ? '★ current TP' : '★ default'}`}
                                </Box>
                              </Box>
                            </Box>
                          );
                        }))}
                      </Stack>
                    </>
                  );
                })()}

                <Typography variant="subtitle2" gutterBottom>Preview</Typography>
                {!selectedPoint ? (
                  <Typography variant="body2" color="text.secondary">
                    Click a point on the chart to select.
                  </Typography>
                ) : previewLoading ? (
                  <Box display="flex" alignItems="center"><CircularProgress size={20} /></Box>
                ) : previewError ? (
                  <Alert severity="error">{previewError}</Alert>
                ) : !previewData ? null : (
                  <Stack spacing={1}>
                    <Typography variant="body2"><b>Date:</b> {selectedPoint.date}</Typography>
                    <Typography variant="body2"><b>Type:</b> {selectedPoint.type}</Typography>
                    <Typography variant="body2"><b>Recorded qty:</b> {selectedPoint.quantity}</Typography>
                    <Typography variant="body2"><b>System stock at TP:</b> {previewData.system_stock_at_tp}</Typography>
                    <Typography variant="body2"><b>Correct stock at TP:</b> {previewData.correct_stock_at_tp}</Typography>
                    <Typography variant="body2">
                      <b>Variance to insert:</b>{' '}
                      <span style={{ color: previewData.variance < 0 ? '#d32f2f' : '#2e7d32' }}>
                        {previewData.variance > 0 ? '+' : ''}{previewData.variance}
                      </span>
                    </Typography>
                    {previewData.would_insert == null ? (
                      <Typography variant="body2" color="text.secondary">
                        No-op (variance ≈ 0) — applying would not insert any new row.
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Would insert at {previewData.would_insert.effective_date}.
                      </Typography>
                    )}
                    {(previewData.existing_adjustments_to_delete || []).length > 0 && (
                      <Typography variant="body2" color="warning.main">
                        ⚠ Replaces {previewData.existing_adjustments_to_delete.length} existing adjustment(s)
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}

          {applyError && (
            <Alert severity="error" sx={{ mb: 1 }}>{applyError}</Alert>
          )}
          {selectedPoint && previewError && !previewLoading && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Preview failed for the selected trust point. You can still apply — the backend
              will recompute variance independently and bail to no-op if it's effectively zero.
              Today's backup is in place if rollback is needed.
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="space-between">
            {isDirectMode ? (
              <Box /> // spacer so the action buttons stay right-aligned
            ) : (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
                disabled={currentIndex === 0}
              >
                Back
              </Button>
            )}
            <Stack direction="row" spacing={1}>
              {!isDirectMode && (
                <Button onClick={() => setCurrentIndex(i => i + 1)} variant="outlined">
                  Skip
                </Button>
              )}
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleAcceptCurrentAndNext}
                disabled={applying || trustPointsData?.active_trust_point_id == null}
                title={
                  applying ? 'Working…' :
                  trustPointsData?.active_trust_point_id == null
                    ? 'No trust point candidates available.'
                    : trustPointsData?.applied_trust_point_id != null
                      ? "Mark the current trust point as reviewed-and-fine. No stock change. Row dismissed from queue."
                      : isDirectMode
                        ? "Store the auto-selected trust point. (Same effect as Apply on this default — backup required.)"
                        : "Store the auto-selected trust point and move to the next item. (Same effect as Apply on this default — backup required.)"
                }
              >
                {applying ? 'Working…' : (isDirectMode ? '✓ Accept Current' : '✓ Accept Current & Next')}
              </Button>
              <Button
                variant="contained"
                endIcon={isDirectMode ? null : <ArrowForwardIcon />}
                onClick={handleApplyAndNext}
                disabled={applying || !selectedPoint || !backupStatus?.success}
                title={
                  applying ? 'Applying…' :
                  !backupStatus?.success ? 'Click ⚠ Backup inventory_adjustments now (top-right) before applying.' :
                  !selectedPoint ? 'Pick a trust point first.' :
                  previewLoading ? 'Preview still loading — you can still apply; the server will recompute variance.' :
                  previewError ? 'Preview failed — clicking will apply anyway; the server computes variance independently.' :
                  !previewData ? 'Preview unavailable — variance will be computed server-side on apply.' :
                  (isDirectMode ? 'Apply this trust point.' : 'Apply this trust point and advance to the next item.')
                }
              >
                {applying ? 'Applying…' : (isDirectMode ? 'Apply Trust Point' : 'Apply & Next')}
              </Button>
            </Stack>
          </Stack>
          {!backupStatus?.success && (
            <Alert severity={backupStatus?.error ? 'error' : 'info'} sx={{ mt: 1 }}>
              {backupStatus?.error
                ? <>Auto-backup failed: <b>{backupStatus.error}</b>. Click the orange button at top-right to retry.</>
                : backupBusy
                  ? <>Auto-backing up <b>inventory_adjustments</b>… Apply will enable once this completes.</>
                  : <>Initialising — Apply will enable once today's auto-backup completes.</>
              }
            </Alert>
          )}
          {!isDirectMode && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Shortcuts: Enter = Apply & Next · S = Skip · B = Back · ← / → = cycle points
            </Typography>
          )}
          {isDirectMode && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Shortcuts: Enter = Apply · ← / → = cycle points
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
